import { useCallback, useEffect, useRef } from 'react'

import { DesktopGridItem } from 'shared/types/desktop-grid'

import { useDesktopStore } from '../../../stores'
import {
  clearNonProfileMemory,
  ensureBaseLayout,
  resolveLayoutMode,
  setLayoutMode,
  updateBaseLayout
} from '../utils'

type UseBaseLayoutOptions = {
  items: DesktopGridItem[]
  cols: number
  rows: number
}

/**
 * Exposes `commitManualItems` (the setter every manual mutation goes through)
 * and keeps the persisted base layout in sync as items/grid dimensions change.
 */
export const useBaseLayout = ({ items, cols, rows }: UseBaseLayoutOptions) => {
  const setItems = useDesktopStore(state => state.setItems)
  const pendingBaseLayoutUpdateRef = useRef<DesktopGridItem[] | null>(null)

  const commitManualItems = useCallback(
    (
      value:
        | DesktopGridItem[]
        | ((prevItems: DesktopGridItem[]) => DesktopGridItem[])
    ) => {
      if (typeof value === 'function') {
        setItems(prevItems => {
          const nextItems = value(prevItems)
          if (nextItems !== prevItems) {
            clearNonProfileMemory()
            pendingBaseLayoutUpdateRef.current = nextItems
          }
          return nextItems
        })
        return
      }

      clearNonProfileMemory()
      pendingBaseLayoutUpdateRef.current = value
      setItems(value)
    },
    [setItems]
  )

  // Flush a scheduled base-layout update once the store reflects it.
  useEffect(() => {
    if (!pendingBaseLayoutUpdateRef.current) return
    if (pendingBaseLayoutUpdateRef.current !== items) return
    if (cols <= 0 || rows <= 0) return
    // Mode follows the device class (viewport), not the grid element — the
    // container can measure transiently narrow during boot, and flushing a
    // commit into the sequential store then would strand it there.
    setLayoutMode(resolveLayoutMode(window.innerWidth))
    updateBaseLayout(items, cols, rows)
    pendingBaseLayoutUpdateRef.current = null
  }, [items, cols, rows])

  // Seed the base layout once items are loaded and the grid is ready, so saved
  // positions survive a page refresh. Items are always loaded from (and laid
  // out for) the current viewport class's storage slot, so they seed that
  // mode's store — seeding the other store would anchor e.g. mobile packed
  // positions as the desktop arrangement.
  useEffect(() => {
    if (items.length === 0 || cols <= 0 || rows <= 0) return
    setLayoutMode(resolveLayoutMode(window.innerWidth))
    ensureBaseLayout(items, cols, rows)
  }, [items, cols, rows])

  return commitManualItems
}
